import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
  useEffect,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SortPayload } from 'react-admin';
import {
  useGetConversations,
  useGetConversation,
} from '@/features/conversations/conversations.hook';
import {
  Conversation,
  ConversationFilters,
  UseConversationsProps,
} from '@/features/conversations/conversations.types';
import { FilterPayload } from 'react-admin';

// Convert our filters to the format expected by the dataProvider
const getFilterPayload = (filters: ConversationFilters): FilterPayload => {
  // Extract defined filter values using object destructuring
  const {
    status,
    is_read,
    has_agent_active,
    has_attachments,
    isMember,
    searchTerm,
  } = filters || {};

  // Build filter payload, only including defined values
  const filterPayload: Record<string, string | boolean> = {
    ...(status && { status }),
    ...(is_read !== undefined && { is_read }),
    ...(has_agent_active !== undefined && { has_agent_active }),
    ...(has_attachments !== undefined && { has_attachments }),
    ...(isMember !== undefined && { isMember }),
    ...(searchTerm && { q: searchTerm }),
  };

  return filterPayload;
};

// Context type definition
interface ConversationsContextType {
  conversations: Conversation[];
  total: number;
  loading: boolean;
  error: Error | null;
  page: number;
  perPage: number;
  setPage: (page: number) => void;
  sort: SortPayload;
  setSort: (sort: SortPayload) => void;
  filters: ConversationFilters;
  setFilters: (filters: Partial<ConversationFilters>) => void;
  selectedConversationId: string | null | undefined;
  setSelectedConversationId: (id: string | null) => void;
  selectedConversation: Conversation | null | undefined;
  refreshConversations: () => void;
}

// Create the context with a default undefined value
const ConversationsContext = createContext<
  ConversationsContextType | undefined
>(undefined);

// Provider props type
interface ConversationsProviderProps {
  children: ReactNode;
  initialProps?: UseConversationsProps;
  autoSelectFirstConversation?: boolean;
}

// Create the provider component
export const ConversationsProvider: React.FC<ConversationsProviderProps> = ({
  children,
  initialProps = {},
  autoSelectFirstConversation = true,
}) => {
  const {
    perPage: initialPerPage = 10,
    initialSort = { field: 'last_message_at', order: 'DESC' },
    initialFilter = {},
  } = initialProps;

  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [perPage] = useState(initialPerPage);
  const [sort, setSort] = useState<SortPayload>(initialSort);
  const [filters, setFilters] = useState<ConversationFilters>(initialFilter);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null | undefined
  >(null);

  const {
    data: conversationsResponse,
    isLoading: loading,
    error,
    refetch,
  } = useGetConversations({
    filter: getFilterPayload(filters),
    sort: sort.field,
    order: sort.order,
    perPage,
    page,
  });

  // Manual refresh function
  const refreshConversations = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['conversations'],
    });
    // refetch();
  }, [queryClient, refetch]);

  // Check if selected conversation is in the paginated list
  const conversationFromList = useMemo(() => {
    if (!selectedConversationId) return null;
    return (
      conversationsResponse?.data?.find(
        (c) => c.id === selectedConversationId,
      ) || null
    );
  }, [conversationsResponse?.data, selectedConversationId]);

  // Only fetch by ID if we have an ID and it's not in the list
  const idToFetch =
    selectedConversationId && !conversationFromList
      ? selectedConversationId
      : null;

  // Fetch conversation by ID only when it's not in the paginated list
  const { data: fetchedConversation } = useGetConversation(idToFetch);

  // Get selected conversation - first try the paginated list, then fall back to fetched
  const selectedConversation = useMemo(() => {
    if (conversationFromList) return conversationFromList;
    if (
      fetchedConversation &&
      fetchedConversation.id === selectedConversationId
    ) {
      return fetchedConversation;
    }
    return undefined;
  }, [conversationFromList, selectedConversationId, fetchedConversation]);

  // Handle setting page and clear selected conversation
  const handleSetPage = useCallback((newPage: number) => {
    setSelectedConversationId(null);
    setPage(newPage);
  }, []);

  // Handle setting filters
  const handleSetFilters = useCallback(
    (newFilters: Partial<ConversationFilters>) => {
      setPage(1); // Reset to page 1 when filters change
      setFilters((prev) => ({ ...prev, ...newFilters }));
    },
    [],
  );

  // Create the context value object
  const contextValue: ConversationsContextType = {
    conversations: conversationsResponse?.data || [],
    total:
      conversationsResponse?.total ||
      conversationsResponse?.pageInfo?.totalItems ||
      0,
    loading: loading,
    error: error as Error,
    page,
    perPage,
    setPage: handleSetPage,
    sort,
    setSort,
    filters,
    setFilters: handleSetFilters,
    selectedConversationId,
    setSelectedConversationId,
    selectedConversation,
    refreshConversations,
  };

  // useMessageListener({
  //   conversationId: selectedConversationId || '',
  // });

  // useSSEConversations(selectedConversationId);

  // Set initial conversation if none selected and conversations are loaded
  useEffect(() => {
    if (
      conversationsResponse?.data?.length &&
      conversationsResponse?.data?.length > 0 &&
      !selectedConversationId &&
      autoSelectFirstConversation
    ) {
      setSelectedConversationId(conversationsResponse?.data[0].id);
    }
  }, [conversationsResponse?.data, autoSelectFirstConversation]);

  return (
    <ConversationsContext.Provider value={contextValue}>
      {children}
    </ConversationsContext.Provider>
  );
};

// Custom hook to use the context
export const useConversationsContext = (): ConversationsContextType => {
  const context = useContext(ConversationsContext);

  if (context === undefined) {
    throw new Error(
      'useConversationsContext must be used within a ConversationsProvider',
    );
  }

  return context;
};
